//#region react import
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
//#endregion

//#region functions import
import ScrollToTopController from "../../Helper/CustomHook/ScrollToTopController";
import { getTranslations } from "../../Helper/TranslationController";
//#endregion

//#region components import
import GroupedButton from "../../Components/GroupedButton/GroupedButton.component";
import GstaLoaderPage from "../../Components/GstaLoaderPage/GstaLoaderPage.component";
import GstaToolTip from "../../Components/GstaToolTip/GstaToolTip.component";
import PdfSettingSection from "../../Components/PdfSettingSection/PdfSettingSection.component";
import UndisplayFeature from "../../Components/UndisplayFeature/UndisplayFeature.component";
import CenterParameters from "../../Pages/Center/CenterParameters/CenterParameters.component";
//#endregion

//#region services import
import { setErrorNotification, setSuccessNotification } from "../../ReduxStore/notificationSlice";
import { setNotationType, setSetting } from "../../ReduxStore/settingSlice";
import { getCenterSettings, setCenterSettings } from "../../Services/SettingsService";
//#endregion

//#region constants import
import {
  CHRONOMETER_VALUES,
  EDIT_STUDENT_CONFIG_COMMAND,
  EXAMINE_MODULES_EXTENDED,
  HOURS_TO_MINUTES,
  MEASURING_SYSTEM_CODE,
  MODIFY_COMMAND,
  MODIFY_EXAMINE_MODULES,
  SHOW_NOTATION_CODE,
  STUDENT_CHRONO,
  TOKEN_VALIDITY_TIME,
  TOKEN_VALIDITY_TIME_VALUES,
  TRAINING_IA_DURATION,
  TRAINING_IA_DURATION_HOURS,
  TRAINING_IA_DURATION_HOURS_EXCLUDING_ZERO,
  TRAINING_IA_DURATION_MINUTES,
  TRAINING_IA_DURATION_MINUTES_EXCLUDING_ZERO,
  TYPE_NOTATION_CODE,
} from "../../Constants/SettingsConstants";
//#endregion

//#region style import
import AcreosSelect from "../../Components/AcreosSelect/AcreosSelect.component";
import { TRANSLATIONS } from "../../Constants/Translations";
import "./CenterParametersLayout.style.scss";
//#endregion

/**
 * Layout of the center parameters page
 */

const CenterParametersLayout = ({ title }) => {
  //#region useState
  const [simulatorNotation, setSimulatorNotation] = useState(false);
  const [simulatorNotationType, setSimulatorNotationType] = useState("");
  const [simulatorMeasuringSystem, setSimulatorMeasuringSystem] = useState("");
  const [configCommand, setConfigCommand] = useState("");
  const [examineModules, setExamineModules] = useState("");
  const [tokenValidityTime, setTokenValidityTime] = useState("");
  const [chronometerValue, setChronometerValue] = useState("");
  const [iaDurationHours, setIaDurationHours] = useState("");
  const [iaDurationMinutes, setIaDurationMinutes] = useState("");
  const [iaDurationTime, setIaDurationTime] = useState("");
  const [hoursOptions, setHoursOptions] = useState([]);
  const [minutesOptions, setMinutesOptions] = useState([]);
  //#endregion

  //#region useSelector
  const translations = useSelector((state) => state.translationSlice.translations);
  const centerId = useSelector((state) => state.connexionSlice.trainer?.activeCenter?.id);
  const translationDictionariesToLoad = useSelector((state) => state.translationSlice.translationsDictionariesToLoad);
  //#endregion

  //#region others use...
  const dispatch = useDispatch();
  //#endregion

  //#region functions
  const handleChangeShowSimulatorNotationValue = (newValue) => {
    setSimulatorNotation(newValue === "true");
  };

  const handleChangeSetting = async (settingCode, settingValue, setSettingValue, dispatchSetting) => {
    try {
      const newValue = (await setCenterSettings(centerId, settingValue, settingCode)).dataModified;
      setSettingValue(newValue);
      dispatchSetting && dispatch(dispatchSetting(newValue));
      dispatch(setSuccessNotification(getTranslations("notification_parameter_updated", translations)));
    } catch (e) {
      dispatch(setErrorNotification(e));
    }
  };

  const handleChangeShowSimulatorNotation = () => {
    handleChangeSetting(SHOW_NOTATION_CODE, !simulatorNotation, handleChangeShowSimulatorNotationValue, () =>
      setSetting({ settingName: SHOW_NOTATION_CODE, settingValue: !simulatorNotation }),
    );
  };

  const handleChangeSimulatorNotationType = (notationType) => {
    handleChangeSetting(TYPE_NOTATION_CODE, notationType, setSimulatorNotationType, setNotationType);
  };

  const handleChangeSimulatorMeasuringSystem = (measuringSystem) => {
    handleChangeSetting(MEASURING_SYSTEM_CODE, measuringSystem, setSimulatorMeasuringSystem, () =>
      setSetting({ settingName: MEASURING_SYSTEM_CODE, settingValue: measuringSystem }),
    );
  };

  const handleChangeConfigCommand = (configCommand) => {
    handleChangeSetting(EDIT_STUDENT_CONFIG_COMMAND, configCommand, setConfigCommand, () =>
      setSetting({ settingName: EDIT_STUDENT_CONFIG_COMMAND, settingValue: configCommand }),
    );
  };

  const handleChangeTokenValidityTime = (tokenValidityTimeNewValue) => {
    handleChangeSetting(TOKEN_VALIDITY_TIME, tokenValidityTimeNewValue, setTokenValidityTime, () =>
      setSetting({ settingName: TOKEN_VALIDITY_TIME, settingValue: tokenValidityTimeNewValue }),
    );
  };

  const handleChangeChronometerValue = (newChronometerValue) => {
    handleChangeSetting(STUDENT_CHRONO, newChronometerValue, setChronometerValue, () =>
      setSetting({ settingName: STUDENT_CHRONO, settingValue: newChronometerValue }),
    );
  };

  const handleChangeIaDurationHoursValue = (newIaDurationHourValue) => {
    let minutes = iaDurationMinutes;

    if (newIaDurationHourValue === "0") {
      setMinutesOptions(TRAINING_IA_DURATION_MINUTES_EXCLUDING_ZERO);

      if (minutes === "0") {
        minutes = TRAINING_IA_DURATION_MINUTES_EXCLUDING_ZERO[0].value;
        setIaDurationMinutes(minutes);
      }
    } else {
      setMinutesOptions(TRAINING_IA_DURATION_MINUTES);
    }
    const hoursToMinutes =
      HOURS_TO_MINUTES.find((obj) => obj.hours.toString() === newIaDurationHourValue.toString())?.minutes ?? 0;
    setIaDurationHours(newIaDurationHourValue);
    const totalTime = Number(hoursToMinutes) + Number(minutes);
    handleChangeSetting(TRAINING_IA_DURATION, totalTime, setIaDurationTime, () =>
      setSetting({
        settingName: TRAINING_IA_DURATION,
        settingValue: totalTime,
      }),
    );
  };

  const handleChangeIaDurationMinutesValue = (newIaDurationMinutesValue) => {
    let hours = iaDurationHours;

    if (newIaDurationMinutesValue === "0") {
      setHoursOptions(TRAINING_IA_DURATION_HOURS_EXCLUDING_ZERO);

      if (hours === "0") {
        hours = TRAINING_IA_DURATION_HOURS_EXCLUDING_ZERO[0].value;
        setIaDurationHours(hours);
      }
    } else {
      setHoursOptions(TRAINING_IA_DURATION_HOURS);
    }

    const hoursToMinutes = HOURS_TO_MINUTES.find((obj) => obj.hours.toString() === hours.toString())?.minutes ?? 0;
    setIaDurationMinutes(newIaDurationMinutesValue);

    const totalTime = Number(hoursToMinutes) + Number(newIaDurationMinutesValue);
    handleChangeSetting(TRAINING_IA_DURATION, totalTime, setIaDurationTime, () =>
      setSetting({
        settingName: TRAINING_IA_DURATION,
        settingValue: totalTime,
      }),
    );
  };

  const handleChangeExamineModulesAuthorization = (newValue) => {
    handleChangeSetting(EXAMINE_MODULES_EXTENDED, newValue, setExamineModules, () =>
      setSetting({ settingName: EXAMINE_MODULES_EXTENDED, settingValue: newValue }),
    );
  };

  const checkInstalledMachines = () => {
    return !translationDictionariesToLoad.some((machine) => machine.acronym === "SYL" || machine.acronym === "LOG");
  };

  const onGetIaDurationSuccess = (settingIaDurationData) => {
    const durationInMinutes = Number(settingIaDurationData.value);

    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    setIaDurationHours(hours.toString());
    setIaDurationMinutes(minutes.toString());

    minutes === 0
      ? setHoursOptions(TRAINING_IA_DURATION_HOURS_EXCLUDING_ZERO)
      : setHoursOptions(TRAINING_IA_DURATION_HOURS);
    hours === 0
      ? setMinutesOptions(TRAINING_IA_DURATION_MINUTES_EXCLUDING_ZERO)
      : setMinutesOptions(TRAINING_IA_DURATION_MINUTES);
  };
  //#endregion

  //#region useQuery
  const { isFetched: isSettingShowNoteFetched, isError: isSettingShowNoteError } = useQuery({
    queryKey: ["settingShowNote"],
    queryFn: () => getCenterSettings(centerId, SHOW_NOTATION_CODE),
    meta: {
      onSuccess: (settingShowNote) => setSimulatorNotation(settingShowNote.value === "true"),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingNotationTypeFetched, isError: isSettingNotationTypeError } = useQuery({
    queryKey: ["settingNotationType"],
    queryFn: () => getCenterSettings(centerId, TYPE_NOTATION_CODE),
    refetchOnWindowFocus: false,
    meta: {
      onSuccess: (settingNotationType) => {
        setSimulatorNotationType(settingNotationType.value);
        dispatch(setNotationType(settingNotationType.value));
      },
      errorMessage: "center_parameters_error",
    },
  });

  const { isFetched: isSettingMeasuringSystemCodeFetched, isError: isSettingMeasuringSystemCodeError } = useQuery({
    queryKey: ["settingMeasuringSystemCode"],
    queryFn: () => getCenterSettings(centerId, MEASURING_SYSTEM_CODE),
    meta: {
      onSuccess: (settingMeasuringSystemCode) => setSimulatorMeasuringSystem(settingMeasuringSystemCode.value),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingExaminesModulesFetched, isError: isSettingExaminesModulesError } = useQuery({
    queryKey: ["settingExaminesModules"],
    queryFn: () => getCenterSettings(centerId, EXAMINE_MODULES_EXTENDED),
    meta: {
      onSuccess: (settingExaminesModules) => setExamineModules(settingExaminesModules.value),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingConfigCommandFetched, isError: isSettingConfigCommandError } = useQuery({
    queryKey: ["settingConfigCommand"],
    queryFn: () => getCenterSettings(centerId, EDIT_STUDENT_CONFIG_COMMAND),
    meta: {
      onSuccess: (settingConfigCommand) => setConfigCommand(settingConfigCommand.value),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingTokenValidityFetched, isError: isSettingTokenValidityError } = useQuery({
    queryKey: ["settingTokenValidity"],
    queryFn: () => getCenterSettings(centerId, TOKEN_VALIDITY_TIME),
    meta: {
      onSuccess: (settingTokenValidity) => setTokenValidityTime(settingTokenValidity.value),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingChronometerFetched, isError: isSettingChronometerError } = useQuery({
    queryKey: ["settingChronometer"],
    queryFn: () => getCenterSettings(centerId, STUDENT_CHRONO),
    meta: {
      onSuccess: (settingChronometer) => setChronometerValue(settingChronometer.value),
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });

  const { isFetched: isSettingIaDurationFetched, isError: isSettingIaDurationError } = useQuery({
    queryKey: ["settingIaDuration"],
    queryFn: () => getCenterSettings(centerId, TRAINING_IA_DURATION),
    meta: {
      onSuccess: onGetIaDurationSuccess,
      errorMessage: "center_parameters_error",
    },
    refetchOnWindowFocus: false,
  });
  //#endregion

  return (
    <div>
      <ScrollToTopController />
      {!isSettingShowNoteFetched &&
        !isSettingNotationTypeFetched &&
        !isSettingMeasuringSystemCodeFetched &&
        !isSettingExaminesModulesFetched &&
        !isSettingConfigCommandFetched &&
        !isSettingTokenValidityFetched &&
        !isSettingChronometerFetched && <GstaLoaderPage />}
      <h2 className="center-parameters-title page-title">{title}</h2>
      <div className="center-parameters-content">
        <h1>{getTranslations("center_notations", translations)}</h1>
        <hr className="hr-without-margin-top" />
        <div className="notation-grid">
          <CenterParameters
            handleChangeShowSimulatorNotation={handleChangeShowSimulatorNotation}
            handleChangeSimulatorMeasuringSystem={handleChangeSimulatorMeasuringSystem}
            handleChangeSimulatorNotationType={handleChangeSimulatorNotationType}
            simulatorNotation={simulatorNotation}
            simulatorNotationType={simulatorNotationType}
            simulatorMeasuringSystem={simulatorMeasuringSystem}
            isSettingNotationTypeError={isSettingNotationTypeError}
            isSettingMeasuringSystemCodeError={isSettingMeasuringSystemCodeError}
            isSettingShowNoteError={isSettingShowNoteError}
          />
        </div>
      </div>
      <PdfSettingSection />
      <div className="center-parameters-content">
        <h1>{getTranslations("center_settings_modules_authorization", translations)}</h1>
        <hr className="hr-without-margin-top" />
        <div className="modules-authorization_content">
          <GstaToolTip
            disabled={!isSettingExaminesModulesError}
            toolTipContent={getTranslations(TRANSLATIONS.CENTER_PARAMETERS_ERROR, translations)}
          >
            <GroupedButton
              label={getTranslations("free_mode", translations)}
              arrayToMap={MODIFY_EXAMINE_MODULES}
              onClick={handleChangeExamineModulesAuthorization}
              selected={examineModules}
              layoutClass={"radio-buttons"}
              disabled={isSettingExaminesModulesError}
            />
          </GstaToolTip>
          <UndisplayFeature isLocal={false}>
            <GstaToolTip
              disabled={!isSettingIaDurationError}
              toolTipContent={getTranslations(TRANSLATIONS.CENTER_PARAMETERS_ERROR, translations)}
            >
              <div className="ia-duration">
                <label className="ia-duration-label">
                  {getTranslations(TRANSLATIONS.MAX_TIME_TRAINING_IA, translations)}
                </label>
                <div className="ia-duration-select">
                  <AcreosSelect
                    dataTestId={"TRAINING_IA_DURATION_HOURS"}
                    options={hoursOptions}
                    setNewValue={handleChangeIaDurationHoursValue}
                    value={iaDurationHours}
                    defaultText={iaDurationHours}
                    isValid
                  />
                  <AcreosSelect
                    dataTestId={"TRAINING_IA_DURATION_MINUTES"}
                    options={minutesOptions}
                    setNewValue={handleChangeIaDurationMinutesValue}
                    value={iaDurationMinutes}
                    defaultText={iaDurationMinutes}
                    isValid
                  />
                </div>
              </div>
            </GstaToolTip>
          </UndisplayFeature>
          <UndisplayFeature isLocal={checkInstalledMachines()}>
            <GstaToolTip
              disabled={!isSettingConfigCommandError}
              toolTipContent={getTranslations("center_parameters_error", translations)}
            >
              <GroupedButton
                label={getTranslations("student_command_config_modification", translations)}
                arrayToMap={MODIFY_COMMAND}
                onClick={handleChangeConfigCommand}
                selected={configCommand}
                layoutClass={"radio-buttons"}
                disabled={isSettingConfigCommandError}
              />
            </GstaToolTip>
          </UndisplayFeature>
          <GstaToolTip
            disabled={!isSettingTokenValidityError}
            toolTipContent={getTranslations("center_parameters_error", translations)}
          >
            <GroupedButton
              label={getTranslations("token_validity_time", translations)}
              arrayToMap={TOKEN_VALIDITY_TIME_VALUES}
              onClick={handleChangeTokenValidityTime}
              selected={tokenValidityTime}
              layoutClass={"radio-buttons"}
              disabled={isSettingTokenValidityError}
            />
          </GstaToolTip>
          <GstaToolTip
            disabled={!isSettingChronometerError}
            toolTipContent={getTranslations("center_parameters_error", translations)}
          >
            <GroupedButton
              label={getTranslations("active_chronometer_for_student", translations)}
              arrayToMap={CHRONOMETER_VALUES}
              onClick={handleChangeChronometerValue}
              selected={chronometerValue}
              layoutClass={"radio-buttons"}
              disabled={isSettingChronometerError}
            />
          </GstaToolTip>
        </div>
      </div>
    </div>
  );
};

CenterParametersLayout.propTypes = {
  title: PropTypes.string.isRequired,
};

export default CenterParametersLayout;
